// mesh-gallery.ts — the 3D-mesh gallery behind the object card's mesh marker.
//
// Opens a modal showing ONE BAKED THUMBNAIL per attached mesh, grouped by body profile. It is the
// answer to "you say there are 11 variations — what are they?", which the count badge alone cannot
// give: on Clothes, Peasant the three `tunic3` rows are a padded vest and `outfit1` is a full outfit
// with legs and boots, and nothing but a picture tells you that.
//
// THUMBNAILS FOR EVERYONE, 3D WHEN SIGNED IN (owner 2026-09-30). An anonymous request gets pictures
// only — no GLB url — which keeps the published library out of reach of an anonymous id-walking
// scrape. A signed-in viewer's rows also carry `model`, and the gallery opens with the Cosmetics
// Store turntable (shared/mesh-turntable.ts) above the thumbnails; clicking a thumbnail spins that one.
//
// LAZY: nothing is fetched until the viewer clicks, and three.js + the GLB load only when the
// turntable mounts. Paying for WebGL to render a catalog page would be the wrong trade.
//
// THEMING: Bootstrap's .modal-content is white (css/src/10-main-head.css) and the site is dark, so
// the shell is restyled here to the house dark-panel vocabulary — the #22d3ee accent, the slate
// gradients and the inset accent bar lifted from .ft-attached-section-title. The PLATE behind each
// thumbnail stays SWITCHABLE: the art is transparent and runs from near-black leather to pale
// linen, so no single backdrop shows every mesh. The choice persists per viewer.

import { mountTurntable, turntableHeadUp, type TurntableHandle } from '../shared/mesh-turntable';

const MODAL_ID = 'ft-mesh-gallery-modal';
const STYLE_ID = 'ft-mesh-gallery-style';
const PLATE_KEY = 'ft.meshGallery.plate';

// One modal element is shared by every card, so a slow response for the item clicked FIRST could
// repaint the body under the item clicked SECOND — with the title still naming the second one.
// Each open takes a ticket; only the newest may write.
let openToken = 0;

// The live turntable, chained so a mesh switch disposes the previous one before mounting the next
// (a GLB still downloading must never land in a viewer that has moved on).
let turntable: Promise<TurntableHandle | null> | null = null;

interface MeshGalleryBody {
    body: string;
    label: string;
    url: string;
    sha256: string;
}

interface MeshGalleryModel {
    url: string;
    sha256: string;
    /** Approved standard-body fittings of a worn look; `url` is the first. Empty for a held/placed thing. */
    bodies: MeshGalleryBody[];
}

interface MeshGalleryRow {
    id: string;
    variant: string;
    bodyProfile: string;
    quality: string;
    state: string;
    part: string;
    primary: boolean;
    thumbUrl: string | null;
    /** Signed-in viewers only — null for a signed-out viewer or a composed (manifest) row. */
    model?: MeshGalleryModel | null;
}

interface MeshGalleryPayload {
    meshes?: MeshGalleryRow[];
    total?: number;
    inherited?: boolean;
    inheritedFrom?: string | null;
    category?: string;
    signedIn?: boolean;
    loginUrl?: string;
    error?: string;
}

const PROFILE_LABELS: Record<string, string> = {
    any: 'Any body',
    masculine_standard: 'Masculine',
    masculine_strong: 'Masculine (strong)',
    feminine_standard: 'Feminine',
    feminine_strong: 'Feminine (strong)',
};

// Local encoder, NOT a `typeof escapeHtml === 'function' ? … : raw` fallback. Everything here is
// database-derived (variant / quality / state / part / the inherited item's name) and goes in via
// innerHTML, some of it inside src/alt/title attributes — so a fallback that returns the raw string
// when the global is missing is an XSS path, not a graceful degradation.
const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function esc(value: unknown): string {
    return String(value ?? '').replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

/** `metahuman_medieval_tunic1_color2` → `Medieval Tunic1 Color2` — the raw variant key is a slug. */
function prettyVariant(variant: string): string {
    const cleaned = String(variant || '').replace(/^metahuman[_-]/i, '').replace(/[_-]+/g, ' ').trim();
    if (!cleaned) return 'Default';
    return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
}

// Scoped to #ft-mesh-gallery-modal so nothing here can reach the rest of the page. Emitted once.
// The two plate variants are CSS-only and keyed off a data attribute on the modal root, so
// switching is ONE attribute write — no refetch, no re-render, no image reload.
const GALLERY_STYLE = `
#${MODAL_ID} .modal-content {
	background: #111720;
	background-image: linear-gradient(135deg, rgba(34, 211, 238, 0.065) 0%, rgba(148, 163, 184, 0.025) 65%);
	border: 1px solid rgba(34, 211, 238, 0.32);
	border-radius: 0.85rem;
	color: #e2e8f0;
}
#${MODAL_ID} .modal-header {
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 0.85rem 0.85rem 0 0;
	background: linear-gradient(100deg,
		color-mix(in srgb, #22d3ee 38%, #0b0e16) 0%,
		color-mix(in srgb, #22d3ee 18%, #0b0e16) 55%,
		#111720 100%);
	box-shadow: inset 4px 0 0 #22d3ee;
	color: #f8fafc;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.55);
	padding: 0.5rem 1rem;
}
#${MODAL_ID} .modal-title { font-weight: 700; letter-spacing: 0.025em; font-size: 1.1rem; }
#${MODAL_ID} .modal-body { overflow-x: hidden; }
#${MODAL_ID} .ft-mg-group {
	margin: 1rem 0 0.5rem;
	color: #94a3b8;
	font-size: 0.7rem;
	font-weight: 700;
	letter-spacing: 0.065em;
	text-transform: uppercase;
}
#${MODAL_ID} .ft-mg-group span { color: #64748b; }
#${MODAL_ID} .ft-mg-card {
	height: 100%;
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 0.5rem;
	background: rgba(148, 163, 184, 0.06);
}
#${MODAL_ID} .ft-mg-name { color: #e2e8f0; font-size: 0.8rem; font-weight: 600; }
#${MODAL_ID} .ft-mg-note {
	border: 1px solid rgba(34, 211, 238, 0.28);
	border-radius: 0.5rem;
	background: rgba(34, 211, 238, 0.07);
	color: #cbd5e1;
}
#${MODAL_ID} .ft-mg-note strong { color: #f8fafc; }
#${MODAL_ID} .ft-mg-muted { color: #94a3b8; }
#${MODAL_ID} .ft-mg-plate {
	display: block;
	width: 100%;
	height: auto;
	aspect-ratio: 1;
	object-fit: contain;
}
#${MODAL_ID} .ft-mg-empty {
	aspect-ratio: 1;
	color: #64748b;
	background: rgba(15, 23, 42, 0.55);
}
/* The switchable backdrop. Dark suits pale linen and bone; light suits black leather and iron. */
#${MODAL_ID}[data-ft-plate="dark"] .ft-mg-plate {
	background: conic-gradient(#2a323b 0 25%, #232a32 0 50%, #2a323b 0 75%, #232a32 0) 0 0 / 18px 18px;
}
#${MODAL_ID}[data-ft-plate="light"] .ft-mg-plate {
	background: conic-gradient(#eceff3 0 25%, #dfe4ea 0 50%, #eceff3 0 75%, #dfe4ea 0) 0 0 / 18px 18px;
}
#${MODAL_ID} .ft-mg-plate-toggle .btn {
	--bs-btn-color: #cbd5e1;
	--bs-btn-border-color: rgba(255, 255, 255, 0.25);
	--bs-btn-hover-color: #0b0e16;
	--bs-btn-hover-bg: #22d3ee;
	--bs-btn-hover-border-color: #22d3ee;
	--bs-btn-active-color: #0b0e16;
	--bs-btn-active-bg: #22d3ee;
	--bs-btn-active-border-color: #22d3ee;
	font-size: 0.7rem;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	text-shadow: none;
}
/* The turntable reuses the Cosmetics Store viewer (css/src/99d-cosmetics-store.css); these are its tokens. */
#${MODAL_ID} {
	--ft-cs-bg: #0f1117;
	--ft-cs-plate: radial-gradient(120% 90% at 50% 30%, #2a2f3d 0%, #151821 62%, #0c0e13 100%);
	--ft-cs-border: rgba(255, 255, 255, 0.12);
	--ft-cs-text: #eef0f4;
	--ft-cs-muted: #9aa1b2;
	--ft-cs-accent: #22d3ee;
}
/* Sized to the window, not the width: header + viewer + one row of thumbnails fit a desktop screen with no
   scrollbar. A long list still scrolls, below the viewer. */
#${MODAL_ID} .ft-mg-viewer {
	aspect-ratio: auto;
	height: clamp(220px, calc(100vh - 400px), 520px);
	min-height: 0;
	margin-bottom: 0.75rem;
}
/* The Dark / Light plate switch covers the turntable backdrop too. */
#${MODAL_ID}[data-ft-plate="light"] .ft-mg-viewer {
	--ft-cs-plate: radial-gradient(120% 90% at 50% 30%, #ffffff 0%, #e6eaef 62%, #cfd5dd 100%);
	--ft-cs-border: rgba(15, 23, 42, 0.18);
}
#${MODAL_ID}[data-ft-plate="light"] .ft-mg-viewer .ft-cosmetic-viewer__bodies,
#${MODAL_ID}[data-ft-plate="light"] .ft-mg-viewer .ft-cosmetic-viewer__hint { background: rgba(15, 23, 42, 0.82); }
#${MODAL_ID}[data-ft-plate="light"] .ft-mg-viewer .ft-cosmetic-viewer__body:not(.is-active),
#${MODAL_ID}[data-ft-plate="light"] .ft-mg-viewer .ft-cosmetic-viewer__hint { color: #e2e8f0; }
#${MODAL_ID} .ft-mg-viewer-name { color: #e2e8f0; font-size: 0.85rem; font-weight: 600; margin: -0.25rem 0 0.25rem; }
#${MODAL_ID} button.ft-mg-card {
	display: block;
	width: 100%;
	padding: 0;
	text-align: left;
	color: inherit;
	cursor: pointer;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
#${MODAL_ID} button.ft-mg-card:hover { border-color: rgba(34, 211, 238, 0.55); }
#${MODAL_ID} button.ft-mg-card.is-selected { border-color: #22d3ee; box-shadow: 0 0 0 1px #22d3ee; }
#${MODAL_ID} .ft-mg-signin a { color: #22d3ee; font-weight: 600; }
`;

type PlateMode = 'light' | 'dark';

/** Remembered per viewer; dark is the default because it matches the page the modal sits on. */
function storedPlate(): PlateMode {
    try {
        return localStorage.getItem(PLATE_KEY) === 'light' ? 'light' : 'dark';
    } catch {
        return 'dark'; // storage throws in some privacy modes — a preference is never worth an exception
    }
}

function applyPlate(modal: HTMLElement, mode: PlateMode): void {
    modal.dataset.ftPlate = mode;
    // forEach, not for..of: the bundle's tsconfig target has no downlevelIteration, so a NodeList
    // is not iterable here (TS2488).
    modal.querySelectorAll<HTMLElement>('.ft-mg-plate-toggle .btn').forEach((btn) => {
        const active = btn.dataset.ftPlateSet === mode;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', String(active));
    });
    try {
        localStorage.setItem(PLATE_KEY, mode);
    } catch { /* preference is best-effort */ }
}

function ensureStyle(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = GALLERY_STYLE;
    document.head.appendChild(style);
}

function ensureModal(): HTMLElement {
    ensureStyle();
    const existing = document.getElementById(MODAL_ID);
    if (existing) return existing;
    const el = document.createElement('div');
    el.id = MODAL_ID;
    el.className = 'modal fade';
    el.tabIndex = -1;
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title mb-0" data-ft-mesh-gallery-title>3D Meshes</h5>
                    <div class="btn-group btn-group-sm ms-auto me-2 ft-mg-plate-toggle" role="group" aria-label="Thumbnail backdrop">
                        <button type="button" class="btn btn-outline-light" data-ft-plate-set="dark">Dark</button>
                        <button type="button" class="btn btn-outline-light" data-ft-plate-set="light">Light</button>
                    </div>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body" data-ft-mesh-gallery-body></div>
            </div>
        </div>`;
    document.body.appendChild(el);

    el.addEventListener('click', (event) => {
        const btn = event.target instanceof Element
            ? event.target.closest<HTMLElement>('[data-ft-plate-set]')
            : null;
        if (!btn) return;
        applyPlate(el, btn.dataset.ftPlateSet === 'light' ? 'light' : 'dark');
    });
    // Closing the gallery frees the WebGL context and stops any download still landing. The ticket
    // bump makes an in-flight mount for this open discard itself instead of starting after close.
    el.addEventListener('hidden.bs.modal', () => {
        openToken++;
        disposeTurntable();
    });

    applyPlate(el, storedPlate());
    return el;
}

function cardHtml(row: MeshGalleryRow, selectedId: string): string {
    const art = row.thumbUrl
        ? `<img class="ft-mg-plate" src="${esc(row.thumbUrl)}" alt="${esc(prettyVariant(row.variant))}" loading="lazy" width="160" height="160">`
        : '<div class="ft-mg-empty d-flex align-items-center justify-content-center small">No preview yet</div>';
    const flags: string[] = [];
    if (row.primary) flags.push('<span class="badge bg-success">Default</span>');
    if (row.quality && row.quality !== 'medium') flags.push(`<span class="badge bg-secondary">${esc(row.quality)}</span>`);
    if (row.state && row.state !== 'default') flags.push(`<span class="badge bg-dark border">${esc(row.state)}</span>`);
    if (row.part) flags.push(`<span class="badge bg-dark border">${esc(row.part)}</span>`);
    const inner = `
                ${art}
                <div class="p-2">
                    <div class="ft-mg-name text-truncate" title="${esc(row.variant)}">${esc(prettyVariant(row.variant))}</div>
                    ${flags.length ? `<div class="mt-1 d-flex flex-wrap gap-1">${flags.join('')}</div>` : ''}
                </div>`;
    // A spinnable row is a button that loads it into the turntable; the rest stay plain pictures.
    const card = row.model
        ? `<button type="button" class="ft-mg-card${row.id === selectedId ? ' is-selected' : ''}" data-ft-mg-pick="${esc(row.id)}"
                aria-pressed="${row.id === selectedId}" title="View in 3D">${inner}</button>`
        : `<div class="ft-mg-card">${inner}</div>`;
    return `
        <div class="col-4 col-md-3 col-lg-2">
            ${card}
        </div>`;
}

/** The row the turntable opens on: the default look if it can spin, else the first that can. */
function initialRow(rows: MeshGalleryRow[]): MeshGalleryRow | null {
    return rows.find((r) => r.model && r.primary) ?? rows.find((r) => r.model) ?? null;
}

function viewerHtml(row: MeshGalleryRow): string {
    return `
        <div class="ft-cosmetic-viewer ft-mg-viewer" data-ft-mg-viewer>
            ${row.thumbUrl ? `<img class="ft-cosmetic-viewer__poster" src="${esc(row.thumbUrl)}" alt="" data-ft-mg-poster>` : ''}
            <div class="ft-cosmetic-viewer__progress" data-ft-mg-progress><span></span></div>
            <p class="ft-cosmetic-viewer__hint" data-ft-mg-hint hidden>Drag to rotate &middot; Scroll or pinch to zoom &middot; Double-click to reset</p>
            <p class="ft-cosmetic-viewer__error" data-ft-mg-error hidden></p>
            <div data-ft-mg-bodies></div>
        </div>
        <div class="ft-mg-viewer-name" data-ft-mg-viewer-name>${esc(prettyVariant(row.variant))}</div>`;
}

function bodyHtml(payload: MeshGalleryPayload): string {
    const rows = Array.isArray(payload.meshes) ? payload.meshes : [];
    if (!rows.length) return '<p class="ft-mg-muted mb-0">No 3D meshes are attached to this item.</p>';
    const first = initialRow(rows);

    const groups = new Map<string, MeshGalleryRow[]>();
    for (const row of rows) {
        const key = row.bodyProfile || 'any';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(row);
    }

    let html = first ? viewerHtml(first) : '';
    if (!payload.signedIn && payload.loginUrl) {
        html += `<div class="ft-mg-note ft-mg-signin py-2 px-3 mb-2 small"><a href="${esc(payload.loginUrl)}">Sign in</a>`
            + ' to view these meshes in 3D.</div>';
    }
    if (payload.inherited) {
        html += '<div class="ft-mg-note py-2 px-3 mb-2 small">These meshes are inherited from '
            + `<strong>${esc(payload.inheritedFrom || 'its classification')}</strong> — this item has no 3D art of its own.</div>`;
    }
    for (const [profile, groupRows] of groups) {
        if (groups.size > 1) {
            html += `<div class="ft-mg-group">${esc(PROFILE_LABELS[profile] || profile)}`
                + ` <span>(${groupRows.length})</span></div>`;
        }
        html += `<div class="row g-2">${groupRows.map((r) => cardHtml(r, first?.id ?? '')).join('')}</div>`;
    }
    return html;
}

function disposeTurntable(): void {
    const previous = turntable;
    turntable = null;
    void previous?.then((h) => h?.dispose()).catch(() => {});
}

function bodyToggleHtml(bodies: MeshGalleryBody[], active: string): string {
    if (bodies.length < 2) return '';
    return `<div class="ft-cosmetic-viewer__bodies" role="group" aria-label="Body">${bodies.map((b) => `
        <button type="button" class="ft-cosmetic-viewer__body${b.body === active ? ' is-active' : ''}" data-ft-mg-body="${esc(b.body)}"
            aria-pressed="${b.body === active}">${esc(b.label)}</button>`).join('')}</div>`;
}

/**
 * Wire the turntable into a freshly rendered body: mount the opening row, then let a thumbnail click
 * or a body toggle swap the model. `token` ties every async write to the open that started it.
 */
function wireViewer(body: Element, payload: MeshGalleryPayload, itemName: string, token: number): void {
    const viewer = body.querySelector<HTMLElement>('[data-ft-mg-viewer]');
    const rows = (payload.meshes ?? []).filter((r) => r.model);
    let row = initialRow(rows);
    if (!viewer || !row) return;
    const bar = viewer.querySelector<HTMLElement>('[data-ft-mg-progress] span');
    const hint = viewer.querySelector<HTMLElement>('[data-ft-mg-hint]');
    const error = viewer.querySelector<HTMLElement>('[data-ft-mg-error]');
    const bodies = viewer.querySelector<HTMLElement>('[data-ft-mg-bodies]');
    const headUp = turntableHeadUp(payload.category ?? '', `${itemName} ${row.variant}`);

    // Per-switch ticket: a model still loading when the viewer moves on must neither mount nor write
    // its ready/error state over the newer selection.
    let seq = 0;
    const current = (mine: number) => token === openToken && mine === seq;
    const reset = () => {
        viewer.classList.remove('is-ready', 'is-error');
        if (bar) bar.style.width = '0%';
        if (error) error.hidden = true;
        if (hint) hint.hidden = true;
    };
    const show = (model: { url: string; sha256: string }) => {
        const mine = ++seq;
        const previous = turntable;
        reset();
        // A failed mount must not poison the chain: every later switch still gets to mount.
        turntable = (previous ?? Promise.resolve(null)).catch(() => null).then((h) => {
            h?.dispose();
            if (!current(mine)) return null;
            reset(); // the disposed mount may have written its state after the switch
            return mountTurntable(viewer, { ...model, headUp }, (state, detail) => {
                if (!current(mine)) return;
                if (state === 'loading' && bar) bar.style.width = `${Math.round(Number(detail || 0) * 100)}%`;
                if (state === 'ready') {
                    viewer.classList.add('is-ready');
                    if (hint) hint.hidden = false;
                }
                if (state === 'error' && error) {
                    viewer.classList.add('is-error');
                    error.hidden = false;
                    error.textContent = `${String(detail || 'The 3D preview could not load.')} Showing the still image instead.`;
                }
            });
        });
    };

    const select = (next: MeshGalleryRow) => {
        row = next;
        const model = next.model!;
        // The poster is the promised still-image fallback, so it must always be THIS mesh's (or none).
        let poster = viewer.querySelector<HTMLImageElement>('[data-ft-mg-poster]');
        if (next.thumbUrl) {
            if (!poster) {
                poster = document.createElement('img');
                poster.className = 'ft-cosmetic-viewer__poster';
                poster.alt = '';
                poster.dataset.ftMgPoster = '';
                viewer.prepend(poster);
            }
            poster.src = next.thumbUrl;
        } else {
            poster?.remove();
        }
        const name = body.querySelector('[data-ft-mg-viewer-name]');
        if (name) name.textContent = prettyVariant(next.variant);
        if (bodies) bodies.innerHTML = bodyToggleHtml(model.bodies, model.bodies[0]?.body ?? '');
        body.querySelectorAll<HTMLElement>('[data-ft-mg-pick]').forEach((el) => {
            const on = el.dataset.ftMgPick === next.id;
            el.classList.toggle('is-selected', on);
            el.setAttribute('aria-pressed', String(on));
        });
        show(model);
    };

    body.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const toggle = target?.closest<HTMLElement>('[data-ft-mg-body]');
        if (toggle && row?.model && !toggle.classList.contains('is-active')) {
            const fit = row.model.bodies.find((b) => b.body === toggle.dataset.ftMgBody);
            if (!fit) return;
            viewer.querySelectorAll<HTMLElement>('[data-ft-mg-body]').forEach((b) => {
                b.classList.toggle('is-active', b === toggle);
                b.setAttribute('aria-pressed', String(b === toggle));
            });
            show(fit);
            return;
        }
        const pick = target?.closest<HTMLElement>('[data-ft-mg-pick]');
        const next = pick ? rows.find((r) => r.id === pick.dataset.ftMgPick) : undefined;
        if (!next || next.id === row?.id) return;
        select(next);
        viewer.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    select(row);
}

/** Open (or refresh) the gallery for one item. Safe to call repeatedly. */
export async function openMeshGallery(itemId: string, itemName: string): Promise<void> {
    if (!itemId) return;
    const token = ++openToken;
    disposeTurntable();
    const modal = ensureModal();
    const title = modal.querySelector('[data-ft-mesh-gallery-title]');
    const body = modal.querySelector('[data-ft-mesh-gallery-body]');
    if (title) title.textContent = itemName ? `3D Meshes — ${itemName}` : '3D Meshes';
    if (body) body.innerHTML = '<p class="ft-mg-muted mb-0">Loading…</p>';

    // Bootstrap is a CDN global here, as it is for campaign-init's modal. Without it the markup is
    // still in the DOM but inert, so fall back to showing it manually rather than silently no-oping.
    const bs = (window as unknown as { bootstrap?: { Modal?: { getOrCreateInstance(el: Element): { show(): void } } } }).bootstrap;
    if (bs?.Modal) {
        bs.Modal.getOrCreateInstance(modal).show();
    } else {
        modal.classList.add('show');
        modal.style.display = 'block';
    }

    try {
        const url = `${BASE_URL}options/api/mesh_thumbnails_inc.php?item_id=${encodeURIComponent(itemId)}`;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json() as MeshGalleryPayload;
        if (token !== openToken) return; // superseded by a later open
        if (!body) return;
        if (payload.error) {
            body.innerHTML = `<p class="text-danger mb-0">${esc(payload.error)}</p>`;
            return;
        }
        // A fresh root per open: the modal body is reused, so its listeners must not outlive the render.
        body.innerHTML = `<div data-ft-mg-root>${bodyHtml(payload)}</div>`;
        const root = body.querySelector('[data-ft-mg-root]');
        if (root) wireViewer(root, payload, itemName, token);
    } catch (error) {
        if (token === openToken && body) {
            body.innerHTML = '<p class="text-danger mb-0">Could not load the mesh list. Try again in a moment.</p>';
        }
        console.error('[mesh-gallery]', error);
    }
}
